import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Loader2, ShieldAlert } from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { completeGestaoSso, logSsoCallbackIssue } from "@/lib/sso.functions";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/logo";
import { SSO_STATE_KEY } from "@/lib/sso-shared";

export const Route = createFileRoute("/sso/callback")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Concluindo entrada · Beit Tefilah Rechovot" },
      { name: "description", content: "Finalizando o login único com a Gestão de cadastros Beit." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: SsoCallbackPage,
});

function SsoCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    (async () => {
      const params = new URLSearchParams(window.location.search);
      const ticket = params.get("ticket") ?? "";
      const state = params.get("state") ?? "";
      const stored = sessionStorage.getItem(SSO_STATE_KEY) ?? "";
      sessionStorage.removeItem(SSO_STATE_KEY);

      const denied = params.get("error");
      if (denied) {
        void logSsoCallbackIssue({
          data: { reason: "denied_by_gestao", detail: denied.slice(0, 300) },
        });
        return setError(denied);
      }
      if (!ticket) {
        void logSsoCallbackIssue({ data: { reason: "missing_ticket" } });
        return setError("Sessão de login inválida ou expirada. Tente entrar novamente.");
      }
      if (!state || state !== stored) {
        void logSsoCallbackIssue({ data: { reason: "state_mismatch" } });
        return setError("Sessão de login inválida ou expirada. Tente entrar novamente.");
      }

      try {
        const result = await completeGestaoSso({ data: { ticket, state } });
        if (!result.ok) {
          if (result.conflictCode) {
            const params = new URLSearchParams({
              motivo: result.conflictCode,
              ...(result.conflictRef ? { ref: result.conflictRef } : {}),
            });
            window.location.replace(`/acesso/conflito-email?${params.toString()}`);
            return;
          }
          return setError(result.error);
        }

        const { error: otpError } = await supabase.auth.verifyOtp({
          type: "magiclink",
          token_hash: result.tokenHash,
        });
        if (otpError) {
          void logSsoCallbackIssue({
            data: { reason: "session_failed", detail: otpError.message.slice(0, 300) },
          });
          return setError(otpError.message);
        }

        navigate({ to: "/inicio", replace: true });
      } catch {
        setError("Não foi possível concluir a entrada. Tente novamente.");
      }
    })();
  }, [navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12">
      <div className="w-full max-w-md text-center">
        <div className="mb-8 flex justify-center">
          <Logo size={96} />
        </div>
        {error ? (
          <div className="rounded-2xl border border-border/60 bg-card p-8 shadow-lg">
            <ShieldAlert className="mx-auto mb-3 h-8 w-8 text-destructive" aria-hidden="true" />
            <h1 className="mb-2 font-serif text-xl font-semibold text-primary">
              Não foi possível entrar
            </h1>
            <p className="mb-6 text-sm leading-relaxed text-muted-foreground">{error}</p>
            <Button onClick={() => navigate({ to: "/auth", replace: true })} className="w-full">
              Voltar para o login
            </Button>
          </div>
        ) : (
          <div className="rounded-2xl border border-border/60 bg-card p-8 shadow-lg">
            <Loader2
              className="mx-auto mb-3 h-8 w-8 animate-spin text-primary"
              aria-hidden="true"
            />
            <h1 className="font-serif text-xl font-semibold text-primary">
              Concluindo sua entrada…
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Validando seu acesso com o sistema de cadastros da congregação.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
